/*
 * ============================================
 * 🔧 CUSTOM CODE - DO NOT REMOVE
 * ============================================
 * Feature: Naamletters configurator
 * Hoort bij: snippets/custom-naamletters.liquid, assets/custom-naamletters.css,
 *            blok 'naamletters' in sections/main-product.liquid, templates/product.naamletters.json
 * Datum: 2026-09-24 (stofkeuze strikt per letter: 2026-09-25, doorspringen naar volgende letter: 2026-09-26,
 *        teksten vertaalbaar via [data-naamletters-strings]: 2026-09-27,
 *        cijfers en letters met accent + stof vergroten: 2026-09-30)
 *
 * Web Component <naamletters-configurator>:
 * - klant typt per vakje een letter of cijfer (lege vakjes tellen niet mee, vakjes tussen letters worden een spatie);
 *   letters met een accent (Ë, Ö, É ...) blijven staan als ze in de toegestane tekens staan, anders zonder accent;
 * - klant kiest per letter een stof: klik op een letter (of typ hem) en klik daarna een stof in het palet.
 *   Een stofklik geldt alleen voor de geselecteerde letter; het palet toont de stof van die letter.
 *   Na een stofklik springt de selectie door naar de volgende letter die nog geen stof heeft;
 * - het vergrootglas op een stof opent een venster met een grote foto; daar kun je bladeren en de stof kiezen;
 * - het aantal ingevulde letters selecteert de variant "Aantal letters" (prijs) in het Dawn productformulier;
 * - letters + stoffen worden als verborgen line item properties (properties[...]) aan het formulier gekoppeld;
 * - validatie gebeurt in de capture-fase van submit, omdat het Dawn formulier 'novalidate' is.
 * ============================================
 */
if (!customElements.get('naamletters-configurator')) {
  const NAAMLETTERS_DEFAULT_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789ÄËÏÖÜÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÇÑ';
  /* Wat een dode toets tijdelijk in het veld zet voordat de letter volgt (¨ ´ ` ^ ~ ' "), of een los accentteken */
  const NAAMLETTERS_PENDING_ACCENT = /^[\p{Sk}\p{M}'"~]+$/u;

  customElements.define(
    'naamletters-configurator',
    class NaamlettersConfigurator extends HTMLElement {
      constructor() {
        super();
        this.boxes = [];
        this.targetIndex = null; // vakje waarop een stofklik van toepassing is
        this.fabricByPointer = false; // laatste bediening van het palet was klik/tik (true) of toetsenbord (false)
        this.handledFabricInput = null; // stof die in deze taak al verwerkt is (klik + change)
        this.composing = false; // een samengesteld teken (dode toets) is nog niet af
        this.zoomIndex = 0; // stof die in het vergrootvenster staat (index in fabricInputs)
        this.zoomButtons = new Map(); // stof-input -> vergrootknop
        this.currentCount = 0;
        this.currentVariant = null;
        this.configError = null;
        this.productFormErrorShown = false;
        this.onSubmit = this.onSubmit.bind(this);
        this.onPaymentButtonClick = this.onPaymentButtonClick.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.form = this.resolveForm();
        /* getAttribute: het formulier bevat een input name="id", waardoor form.id dat element teruggeeft i.p.v. de id-string */
        this.formId = this.form ? this.form.getAttribute('id') : this.dataset.formId;
        this.productForm = this.form ? this.form.closest('product-form') : null;
        this.priceContainer = document.getElementById(`price-${this.dataset.sectionId}`);

        this.boxesContainer = this.querySelector('[data-naamletters-boxes]');
        this.countLabel = this.querySelector('[data-naamletters-count]');
        this.addButton = this.querySelector('[data-naamletters-add]');
        this.removeButton = this.querySelector('[data-naamletters-remove]');
        this.summary = this.querySelector('[data-naamletters-summary]');
        this.errorElement = this.querySelector('[data-naamletters-error]');
        this.propertiesContainer = this.querySelector('[data-naamletters-properties]');
        this.statusElement = this.querySelector('[data-naamletters-status]');
        this.paletteHeading = this.querySelector('[data-naamletters-palette-heading]');
        this.paletteHeadingText = this.paletteHeading ? this.paletteHeading.textContent.trim() : '';
        this.fabricInputs = Array.from(this.querySelectorAll('.naamletters__fabric-input'));
        if (!this.boxesContainer || !this.propertiesContainer) return;

        this.strings = this.readStrings();
        this.variants = this.readVariants();
        this.allowedCharacters = (this.dataset.allowedCharacters || NAAMLETTERS_DEFAULT_CHARACTERS)
          .normalize('NFC')
          .toUpperCase()
          .replace(/\s+/g, '');
        /* Basisprijs (0 letters) en meerprijs per letter, voorgeformatteerd in Liquid */
        this.basePrice = this.dataset.basePrice || '';
        this.pricePerLetter = this.dataset.pricePerLetter || '';

        /* Maximum = instelling, begrensd door de hoogste beschikbare variant "N letters" */
        const configuredMax = parseInt(this.dataset.maxLetters, 10) || 20;
        const highestVariantCount = this.variants.reduce((max, variant) => Math.max(max, variant.count), 0);
        this.maxLetters = Math.max(1, Math.min(configuredMax, highestVariantCount || configuredMax));
        const defaultBoxes = Math.min(Math.max(parseInt(this.dataset.defaultBoxes, 10) || 10, 1), this.maxLetters);

        this.boxesContainer.innerHTML = '';
        this.boxes = [];
        for (let index = 0; index < defaultBoxes; index += 1) this.addBox();

        this.addButton?.addEventListener('click', () => {
          if (this.addBox(true)) this.update();
        });
        this.removeButton?.addEventListener('click', () => {
          if (this.removeBox()) this.update();
        });
        /* 'click' vangt ook een klik op een al geselecteerde stof (geen change-event), 'change' vangt toetsenbord */
        this.fabricInputs.forEach((input) => {
          input.addEventListener('click', () => this.selectFabric(input));
          input.addEventListener('change', () => this.selectFabric(input));
        });
        /* Doorspringen alleen na klik of tik: met de pijltjestoetsen blader je door het palet en blijft de letter staan */
        const palette = this.querySelector('.naamletters__palette');
        palette?.addEventListener('pointerdown', () => {
          this.fabricByPointer = true;
        });
        palette?.addEventListener('keydown', () => {
          this.fabricByPointer = false;
        });
        this.setupZoom();
        (this.productInfo || this.form)?.addEventListener('submit', this.onSubmit, true);
        /* "Direct kopen"-knoppen (dynamic checkout) omzeilen het submit-event; ook die tegenhouden zolang de slinger niet compleet is */
        (this.productInfo || this.form)?.addEventListener('click', this.onPaymentButtonClick, true);

        this.update();
        if (!this.variants.length) this.showError(this.text('errorNoVariants'), true);
      }

      disconnectedCallback() {
        if (this.zoomDialog?.open) this.zoomDialog.close();
        (this.productInfo || this.form)?.removeEventListener('submit', this.onSubmit, true);
        (this.productInfo || this.form)?.removeEventListener('click', this.onPaymentButtonClick, true);
      }

      onPaymentButtonClick(event) {
        if (!event.target.closest || !event.target.closest('.shopify-payment-button')) return;
        const error = this.validate();
        if (!error) return;
        event.preventDefault();
        event.stopPropagation();
        this.showError(error);
      }

      /* ---------- initialisatie ---------- */

      resolveForm() {
        const byId = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
        if (byId && (!this.productInfo || this.productInfo.contains(byId))) return byId;
        return this.productInfo ? this.productInfo.querySelector('form[data-type="add-to-cart-form"]') : byId;
      }

      /* Vertaalde teksten uit de snippet (locales naamletters.*); [naam] wordt hier ingevuld */
      readStrings() {
        const script = this.querySelector('[data-naamletters-strings]');
        if (!script) return {};
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('[naamletters] Teksten konden niet gelezen worden', error);
          return {};
        }
      }

      text(key, values = {}) {
        return Object.entries(values).reduce(
          (result, [name, value]) => result.split(`[${name}]`).join(String(value)),
          this.strings[key] || ''
        );
      }

      /* "1 letter" / "3 letters" in de taal van de winkel */
      letters(count) {
        return this.text(count === 1 ? 'letterCountOne' : 'letterCountOther', { count });
      }

      readVariants() {
        const script = this.querySelector('[data-naamletters-variants]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent)
            .map((variant) => ({ ...variant, count: parseInt(variant.title, 10) }))
            .filter((variant) => Number.isInteger(variant.count) && variant.count > 0);
        } catch (error) {
          console.error('[naamletters] Varianten konden niet gelezen worden', error);
          return [];
        }
      }

      variantForCount(count) {
        return this.variants.find((variant) => variant.count === count) || null;
      }

      /* ---------- vakjes ---------- */

      addBox(focusNew = false) {
        if (this.boxes.length >= this.maxLetters) return false;
        const index = this.boxes.length;
        const box = document.createElement('div');
        box.className = 'naamletters__box';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'naamletters__letter';
        input.autocomplete = 'off';
        input.setAttribute('autocapitalize', 'characters');
        input.setAttribute('spellcheck', 'false');
        input.setAttribute('inputmode', 'text');
        input.setAttribute('enterkeyhint', 'next');
        input.setAttribute('aria-label', this.text('letterLabel', { index: index + 1 }));
        input.addEventListener('input', (event) => this.onLetterInput(box, event));
        /* Samengestelde tekens (dode toets " + e = ë, of Option-u op een Mac): na afloop nog een keer verwerken */
        input.addEventListener('compositionstart', () => {
          this.composing = true;
        });
        input.addEventListener('compositionend', () => {
          this.composing = false;
          this.onLetterInput(box);
        });
        input.addEventListener('keydown', (event) => this.onLetterKeydown(event, box));
        input.addEventListener('paste', (event) => this.onPaste(event, box));

        box.appendChild(input);
        /* Klik of tik selecteert het vakje voor de stofkeuze; programmatische focus (doorspringen) niet.
           Bewust 'click' en geen 'pointerdown': een scrollbeweging die op een vakje begint mag niets selecteren. */
        box.addEventListener('click', () => {
          this.setTarget(box);
          input.focus();
        });
        /* Bij focus de letter selecteren, zodat typen de letter vervangt in plaats van ernaast te schrijven */
        input.addEventListener('focus', () => {
          if (input.value) window.setTimeout(() => input.select(), 0);
        });
        this.boxesContainer.appendChild(box);
        this.boxes.push(box);
        this.refreshCount();
        if (focusNew) {
          this.focusBox(index);
          this.setTarget(box);
        }
        return true;
      }

      removeBox() {
        if (this.boxes.length <= 1) return false;
        const box = this.boxes.pop();
        box.remove();
        if (this.targetIndex !== null && this.targetIndex >= this.boxes.length) this.setTarget(null);
        this.refreshCount();
        return true;
      }

      refreshCount() {
        const total = this.boxes.length;
        if (this.countLabel) this.countLabel.textContent = this.text(total === 1 ? 'boxCountOne' : 'boxCountOther', { count: total });
        if (this.addButton) this.addButton.disabled = total >= this.maxLetters;
        if (this.removeButton) this.removeButton.disabled = total <= 1;
      }

      letterOf(box) {
        return box.dataset.letter || '';
      }

      /* Geselecteerd vakje voor de stofkeuze; het palet toont de stof van dit vakje */
      setTarget(box) {
        this.targetIndex = box ? this.boxes.indexOf(box) : null;
        this.boxes.forEach((other) => {
          other.classList.toggle('is-active', other === box);
          other.querySelector('input').setAttribute('aria-current', other === box ? 'true' : 'false');
        });
        if (this.statusElement) {
          this.statusElement.textContent = box ? this.text('letterSelected', { index: this.targetIndex + 1 }) : '';
        }
        /* Paletkop noemt de geselecteerde letter, zodat je ook onderin het palet ziet waar de stof heen gaat */
        if (this.paletteHeading && this.paletteHeadingText) {
          const letter = box ? this.letterOf(box) : '';
          this.paletteHeading.textContent = letter
            ? this.text('paletteHeadingForLetter', { heading: this.paletteHeadingText, index: this.targetIndex + 1, letter })
            : this.paletteHeadingText;
        }
        this.syncPalette();
      }

      get targetBox() {
        return this.targetIndex !== null ? this.boxes[this.targetIndex] || null : null;
      }

      /* Eerste ingevulde letter zonder stof vanaf vakje `start`, daarna verder vooraan; null als alles een stof heeft */
      nextBoxWithoutFabric(start = 0) {
        const total = this.boxes.length;
        for (let offset = 0; offset < total; offset += 1) {
          const box = this.boxes[(start + offset) % total];
          if (this.letterOf(box) && !box.dataset.fabricName) return box;
        }
        return null;
      }

      syncPalette() {
        const fabricName = this.targetBox ? this.targetBox.dataset.fabricName || '' : '';
        this.fabricInputs.forEach((input) => {
          input.checked = Boolean(fabricName) && input.value === fabricName;
        });
        /* Net als de radiogroep maar één tabstop: alleen het vergrootglas van de gekozen (of eerste) stof zit in de tabvolgorde */
        const tabbableIndex = Math.max(
          this.fabricInputs.findIndex((input) => input.checked),
          0
        );
        this.fabricInputs.forEach((input, index) => {
          const button = this.zoomButtons.get(input);
          if (button) button.tabIndex = index === tabbableIndex ? 0 : -1;
        });
      }

      focusBox(index) {
        const box = this.boxes[index];
        if (!box) return;
        const input = box.querySelector('input');
        input.focus();
        if (input.value) input.select();
      }

      /* Tekens in hoofdletters en samengesteld (NFC), zodat een e met los trema-teken één teken Ë is */
      charactersOf(value) {
        return Array.from((value || '').normalize('NFC').toUpperCase());
      }

      /* Toegestaan teken voor één (hoofdletter)teken: met accent als dat mag (Ë), anders zonder accent (Å -> A), anders '' */
      toAllowedCharacter(char) {
        if (this.allowedCharacters.includes(char)) return char;
        const base = char.normalize('NFD').replace(/[̀-ͯ]/g, '');
        return base.length === 1 && this.allowedCharacters.includes(base) ? base : '';
      }

      /* Laatste toegestane teken wint */
      sanitize(value) {
        const allowed = this.charactersOf(value)
          .map((char) => this.toAllowedCharacter(char))
          .filter(Boolean);
        return allowed.length ? allowed[allowed.length - 1] : '';
      }

      setLetter(box, letter) {
        const input = box.querySelector('input');
        if (input.value !== letter) input.value = letter;
        box.dataset.letter = letter;
        box.classList.toggle('has-letter', Boolean(letter));
        box.classList.toggle('is-digit', /^\d$/.test(letter));
        if (!letter) this.clearFabric(box);
      }

      onLetterInput(box, event) {
        const input = box.querySelector('input');
        const raw = input.value.normalize('NFC');
        const previous = this.letterOf(box);
        /* Nieuw getypte tekst = alles behalve de eerder aanwezige letter, waar de cursor ook stond */
        const typed = previous && raw.includes(previous) ? raw.replace(previous, '') : raw;
        /* Accent van een dode toets wacht nog op de letter: het veld nu niet aanpassen, anders valt het accent weg */
        if (event && (event.isComposing || this.composing) && NAAMLETTERS_PENDING_ACCENT.test(typed)) return;
        const typedLetter = this.sanitize(typed);
        const letter = typedLetter || this.sanitize(raw) || (raw ? previous : '');

        this.setLetter(box, letter);
        /* Stofkeuze begint bij de eerste letter zonder stof, zodat je na het typen van de naam meteen stoffen kunt aanklikken */
        this.setTarget(letter ? this.nextBoxWithoutFabric() || box : null);
        if (typedLetter) this.focusBox(this.boxes.indexOf(box) + 1);
        this.update();
      }

      onLetterKeydown(event, box) {
        const index = this.boxes.indexOf(box);
        const input = box.querySelector('input');
        switch (event.key) {
          case 'Backspace':
            if (input.value === '' && index > 0) {
              event.preventDefault();
              this.setLetter(this.boxes[index - 1], '');
              this.focusBox(index - 1);
              this.setTarget(null);
              this.update();
            }
            break;
          case 'ArrowLeft':
            if (index > 0) {
              event.preventDefault();
              this.focusBox(index - 1);
              this.setTarget(this.boxes[index - 1]);
            }
            break;
          case 'ArrowRight':
          case 'Enter':
            event.preventDefault();
            if (index < this.boxes.length - 1) {
              this.focusBox(index + 1);
              this.setTarget(this.boxes[index + 1]);
            }
            break;
          default:
        }
      }

      /* Een geplakte naam wordt over de vakjes verdeeld; spaties worden lege vakjes */
      onPaste(event, box) {
        const text = event.clipboardData ? event.clipboardData.getData('text') : '';
        if (!text) return;
        event.preventDefault();

        const characters = this.charactersOf(text.replace(/\s+/g, ' ').trim());
        let index = this.boxes.indexOf(box);
        let lastFilled = null;

        for (const char of characters) {
          const letter = char === ' ' ? '' : this.toAllowedCharacter(char);
          if (char !== ' ' && !letter) continue;
          if (index >= this.boxes.length && !this.addBox()) break;
          this.setLetter(this.boxes[index], letter);
          if (letter) lastFilled = this.boxes[index];
          index += 1;
        }

        this.focusBox(Math.min(index, this.boxes.length - 1));
        this.setTarget(this.nextBoxWithoutFabric() || lastFilled);
        this.update();
      }

      /* ---------- stoffen: keuze geldt alleen voor het geselecteerde vakje ---------- */

      selectFabric(input) {
        /* Een klik geeft 'click' én 'change' in dezelfde taak; na het doorspringen zou de tweede de stof ook op de volgende letter zetten */
        if (this.handledFabricInput === input) return;
        this.handledFabricInput = input;
        window.setTimeout(() => {
          if (this.handledFabricInput === input) this.handledFabricInput = null;
        }, 0);

        this.chooseFabric(input, this.fabricByPointer);
      }

      /* Stof op de geselecteerde letter zetten; `advance`: daarna door naar de volgende letter zonder stof */
      chooseFabric(input, advance) {
        const target = this.targetBox;
        if (!target) {
          this.syncPalette();
          this.showHint(this.text('hintSelectLetter'));
          return;
        }
        this.applyFabric(target, { name: input.value, image: input.dataset.fabricImage || '' });
        if (advance) {
          const next = this.nextBoxWithoutFabric(this.targetIndex + 1);
          if (next) this.setTarget(next);
        }
        this.syncPalette();
        this.update();
      }

      /* ---------- stof vergroten: <dialog> met grote foto, bladeren en "Kies voor letter n" ---------- */

      setupZoom() {
        this.zoomDialog = this.querySelector('[data-naamletters-zoom-dialog]');
        const buttons = Array.from(this.querySelectorAll('[data-naamletters-zoom]'));
        /* Zonder <dialog>-ondersteuning (zeer oude browsers) geen vergrootglas tonen */
        if (!this.zoomDialog || typeof this.zoomDialog.showModal !== 'function') {
          buttons.forEach((button) => {
            button.hidden = true;
          });
          return;
        }

        buttons.forEach((button) => {
          const input = button.closest('.naamletters__fabric')?.querySelector('.naamletters__fabric-input');
          if (!input) return;
          this.zoomButtons.set(input, button);
          button.addEventListener('click', () => this.openZoom(input));
        });

        this.zoomImage = this.zoomDialog.querySelector('[data-naamletters-zoom-image]');
        this.zoomName = this.zoomDialog.querySelector('[data-naamletters-zoom-name]');
        this.zoomChooseButton = this.zoomDialog.querySelector('[data-naamletters-zoom-choose]');
        this.zoomNote = this.zoomDialog.querySelector('[data-naamletters-zoom-note]');
        this.zoomCloseButton = this.zoomDialog.querySelector('[data-naamletters-zoom-close]');

        this.zoomCloseButton?.addEventListener('click', () => this.zoomDialog.close());
        this.zoomDialog.querySelector('[data-naamletters-zoom-previous]')?.addEventListener('click', () => {
          this.showZoomFabric(this.zoomIndex - 1);
        });
        this.zoomDialog.querySelector('[data-naamletters-zoom-next]')?.addEventListener('click', () => {
          this.showZoomFabric(this.zoomIndex + 1);
        });
        this.zoomChooseButton?.addEventListener('click', () => {
          const input = this.fabricInputs[this.zoomIndex];
          this.zoomDialog.close();
          if (input) this.chooseFabric(input, true);
        });
        this.zoomDialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.showZoomFabric(this.zoomIndex - 1);
          else if (event.key === 'ArrowRight') this.showZoomFabric(this.zoomIndex + 1);
        });
        /* Klik op de donkere achtergrond (buiten het venster) sluit; het venster zelf vult de <dialog> helemaal */
        this.zoomDialog.addEventListener('click', (event) => {
          if (event.target === this.zoomDialog) this.zoomDialog.close();
        });
        this.zoomDialog.addEventListener('close', () => {
          document.body.classList.remove('overflow-hidden');
        });
        this.syncPalette();
      }

      openZoom(input) {
        if (!this.zoomDialog || this.zoomDialog.open) return;
        this.showZoomFabric(this.fabricInputs.indexOf(input));
        this.zoomDialog.showModal();
        document.body.classList.add('overflow-hidden');
        const focusTarget = this.zoomChooseButton && !this.zoomChooseButton.hidden ? this.zoomChooseButton : this.zoomCloseButton;
        focusTarget?.focus();
      }

      showZoomFabric(index) {
        const total = this.fabricInputs.length;
        if (!total) return;
        this.zoomIndex = (index + total) % total;
        const input = this.fabricInputs[this.zoomIndex];
        const image = input.dataset.fabricImageLarge || input.dataset.fabricImage || '';

        if (this.zoomImage) {
          this.zoomImage.hidden = !image;
          if (image) {
            /* Breedte/hoogte van de foto vooraf zetten, zodat het venster niet verspringt tijdens het laden */
            if (input.dataset.fabricImageWidth) this.zoomImage.width = input.dataset.fabricImageWidth;
            if (input.dataset.fabricImageHeight) this.zoomImage.height = input.dataset.fabricImageHeight;
            this.zoomImage.src = image;
            this.zoomImage.alt = input.value;
          } else {
            this.zoomImage.removeAttribute('src');
          }
        }
        if (this.zoomName) this.zoomName.textContent = input.value;
        this.renderZoomChoice();
      }

      /* Knop "Kies voor letter n (X)"; zonder geselecteerd vakje een uitleg in plaats van de knop */
      renderZoomChoice() {
        if (!this.zoomChooseButton) return;
        const target = this.targetBox;
        this.zoomChooseButton.hidden = !target;
        if (this.zoomNote) {
          this.zoomNote.hidden = Boolean(target);
          this.zoomNote.textContent = target ? '' : this.text('zoomNoLetter');
        }
        if (!target) return;
        const letter = this.letterOf(target);
        this.zoomChooseButton.textContent = letter
          ? this.text('zoomChooseLetter', { index: this.targetIndex + 1, letter })
          : this.text('zoomChooseBox', { index: this.targetIndex + 1 });
      }

      applyFabric(box, fabric) {
        box.dataset.fabricName = fabric.name;
        box.classList.add('has-fabric');
        box.style.setProperty(
          '--naamletters-fabric-image',
          fabric.image
            ? `url("${fabric.image}")`
            : 'linear-gradient(135deg, rgba(var(--color-foreground), 0.12), rgba(var(--color-foreground), 0.35))'
        );
        box.querySelector('input').setAttribute('title', fabric.name);
      }

      clearFabric(box) {
        delete box.dataset.fabricName;
        box.classList.remove('has-fabric');
        box.style.removeProperty('--naamletters-fabric-image');
        box.querySelector('input').removeAttribute('title');
      }

      /* ---------- status naar formulier, prijs en samenvatting ---------- */

      update() {
        const letters = this.boxes.map((box) => this.letterOf(box));
        const filledBoxes = this.boxes.filter((box) => this.letterOf(box));
        const count = filledBoxes.length;
        const name = letters
          .map((letter) => letter || ' ')
          .join('')
          .replace(/\s+/g, ' ')
          .trim();
        const variant = this.variantForCount(count);

        this.currentCount = count;
        this.currentVariant = variant;

        const priceVariant = variant || this.variantForCount(1) || this.variants[0] || null;
        if (variant) this.setVariantId(variant.id);
        if (count === 0 && this.basePrice) this.renderPriceText(this.basePrice);
        else this.renderPrice(priceVariant);
        this.renderSummary(count, priceVariant, variant, filledBoxes);
        this.renderProperties(name, filledBoxes);
        this.hideError();
      }

      setVariantId(id) {
        const forms = [this.form, document.getElementById(`product-form-installment-${this.dataset.sectionId}`)];
        forms.forEach((form) => {
          const input = form ? form.querySelector('input[name="id"]') : null;
          if (!input || String(input.value) === String(id)) return;
          input.value = id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      /* Prijstekst zonder variant (basisprijs bij 0 letters) */
      renderPriceText(text) {
        if (!this.priceContainer) return;
        const price = this.priceContainer.querySelector('.price');
        const regular = this.priceContainer.querySelector('.price__regular .price-item--regular');
        const sale = this.priceContainer.querySelector('.price__sale .price-item--sale');
        if (regular) regular.textContent = text;
        if (sale) sale.textContent = text;
        if (price) price.classList.remove('price--on-sale', 'price--sold-out');
      }

      renderPrice(variant) {
        if (!this.priceContainer || !variant) return;
        const price = this.priceContainer.querySelector('.price');
        const regular = this.priceContainer.querySelector('.price__regular .price-item--regular');
        const sale = this.priceContainer.querySelector('.price__sale .price-item--sale');
        const compareAt = this.priceContainer.querySelector('.price__sale s.price-item--regular');

        if (regular) regular.textContent = variant.price;
        if (sale) sale.textContent = variant.price;
        if (compareAt) compareAt.textContent = variant.compare_at_price || '';
        if (price) {
          price.classList.toggle('price--on-sale', Boolean(variant.compare_at_price));
          price.classList.toggle('price--sold-out', !variant.available);
        }
      }

      renderSummary(count, priceVariant, variant, filledBoxes) {
        if (!this.summary) return;
        const withoutFabric = filledBoxes.filter((box) => !box.dataset.fabricName).length;
        if (!this.variants.length) {
          this.summary.textContent = '';
        } else if (count === 0) {
          if (this.basePrice) {
            this.summary.textContent = this.pricePerLetter
              ? this.text('summaryFromPerLetter', { price: this.basePrice, per_letter: this.pricePerLetter })
              : this.text('summaryFrom', { price: this.basePrice });
          } else {
            this.summary.textContent = priceVariant
              ? this.text('summaryTypeNameFrom', { price: priceVariant.price })
              : this.text('summaryTypeName');
          }
        } else if (!variant) {
          this.summary.textContent = this.text('summaryTooMany', { count, max: this.maxLetters });
        } else if (withoutFabric > 0) {
          this.summary.textContent = this.text('summaryMissingFabric', {
            letters: this.letters(count),
            price: variant.price,
            missing: this.letters(withoutFabric),
          });
        } else {
          this.summary.textContent = this.text('summaryComplete', { letters: this.letters(count), price: variant.price });
        }
      }

      renderProperties(name, filledBoxes) {
        this.propertiesContainer.innerHTML = '';
        if (!filledBoxes.length) return;

        const addProperty = (key, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          input.setAttribute('form', this.formId);
          this.propertiesContainer.appendChild(input);
        };

        addProperty('Naam', name);
        filledBoxes.forEach((box, index) => {
          const letter = this.letterOf(box);
          const fabric = box.dataset.fabricName;
          addProperty(`Letter ${index + 1}`, fabric ? `${letter} – ${fabric}` : letter);
        });
      }

      /* ---------- validatie ---------- */

      validate() {
        if (!this.variants.length) return this.text('errorNotConfigured');
        if (this.currentCount === 0) return this.text('errorNoLetters');

        const missingIndex = this.boxes.findIndex((box) => this.letterOf(box) && !box.dataset.fabricName);
        if (missingIndex !== -1) {
          return this.text('errorMissingFabric', {
            index: missingIndex + 1,
            letter: this.letterOf(this.boxes[missingIndex]),
          });
        }
        if (!this.currentVariant) return this.text('errorMaxLetters', { max: this.maxLetters });
        if (!this.currentVariant.available) return this.text('errorUnavailable');
        return null;
      }

      onSubmit(event) {
        if (this.form && event.target !== this.form) return;
        const error = this.validate();
        if (!error) {
          this.hideError();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.showError(error);

        const boxToFix = this.boxes.find((box) => this.letterOf(box) && !box.dataset.fabricName);
        if (boxToFix) {
          this.setTarget(boxToFix);
          this.focusBox(this.boxes.indexOf(boxToFix));
        } else if (this.currentCount === 0) {
          this.focusBox(0);
        }
      }

      showHint(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      showError(message, persistent = false) {
        if (persistent) this.configError = message;
        if (this.errorElement) {
          this.errorElement.textContent = message;
          this.errorElement.hidden = false;
        }
        if (this.productForm && typeof this.productForm.handleErrorMessage === 'function') {
          this.productForm.handleErrorMessage(message);
          this.productFormErrorShown = true;
        }
        if (persistent && this.productForm && typeof this.productForm.toggleSubmitButton === 'function') {
          this.productForm.toggleSubmitButton(true, this.text('unavailable'));
        }
      }

      hideError() {
        if (this.configError) return;
        if (this.errorElement) {
          this.errorElement.hidden = true;
          this.errorElement.textContent = '';
        }
        if (this.productFormErrorShown && this.productForm && typeof this.productForm.handleErrorMessage === 'function') {
          this.productForm.handleErrorMessage();
          this.productFormErrorShown = false;
        }
      }
    }
  );
}
